import { useEffect } from 'react';
import { useParams, Link, Navigate, useLocation } from 'react-router-dom';
import { Phone, MapPin, Clock, Star, CheckCircle, ArrowLeft, Gem } from 'lucide-react';
import { cities, cityPath } from '../cityData';
import { trackEvent } from '../track';

export default function CityPage({ slug: propSlug }) {
  const { citySlug } = useParams();
  const location = useLocation();
  const slug = propSlug || citySlug;
  const city = cities.find((c) => c.slug === slug);

  useEffect(() => {
    if (city) {
      document.title = `Appliance Repair in ${city.name}, FL | Same-Day Service | Appliances Repair MCulai`;
      window.scrollTo(0, 0);
    }
    return () => {
      document.title = 'Appliances Repair MCulai | Same-Day Appliance Repair in South Florida';
    };
  }, [city]);

  if (!city) return <Navigate to="/" replace />;
  if (city.hub && location.pathname !== city.path) return <Navigate to={city.path} replace />;

  return (
    <div data-testid={`city-page-${city.slug}`}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="city-header-logo">
            <img src="/logo.png" alt="Appliances Repair MCulai logo" className="w-10 h-10 object-contain" />
            <div className="leading-tight text-left">
              <span className="block text-sm md:text-lg font-bold text-gray-900">Appliances Repair MCulai</span>
              <span className="hidden sm:block text-xs text-gray-500">Same-Day Service • South Florida</span>
            </div>
          </Link>
          <a
            href="tel:7866964010"
            onClick={() => trackEvent('click_to_call', { page: city.slug })}
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors"
            data-testid="city-header-call-button"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">786-696-4010</span>
            <span className="sm:hidden">Call</span>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-flex items-center gap-1 text-blue-300 hover:text-white text-sm mb-6 transition-colors" data-testid="city-back-link">
            <ArrowLeft className="w-4 h-4" /> All Service Areas
          </Link>
          <h1 data-testid="city-hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Appliance Repair in {city.name}, FL
          </h1>
          <p className="text-base md:text-lg text-gray-200 mb-8 max-w-3xl mx-auto">
            {city.luxury ? city.luxuryNote.split('. ').slice(0, 1).join('. ') + '.' : city.intro.split('. ').slice(0, 1).join('. ') + '.'} Same-day service available, 7 days a week.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <a
              href="tel:7866964010"
              onClick={() => trackEvent('click_to_call', { page: city.slug })}
              data-testid="city-hero-call-button"
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg text-xl font-bold flex items-center gap-2 transition-all transform hover:scale-105 shadow-lg"
            >
              <Phone className="w-6 h-6" />
              Call Now: 786-696-4010
            </a>
            <Link
              to="/book-online"
              onClick={() => trackEvent('begin_booking', { page: city.slug })}
              data-testid="city-hero-book-button"
              className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 rounded-lg text-xl font-bold transition-all transform hover:scale-105 shadow-lg"
            >
              Book Online
            </Link>
          </div>
          <p className="text-gray-300 text-sm">
            <Clock className="inline w-4 h-4 mr-1" />
            Sun–Fri 7:30 AM – 9 PM • Sat 8 AM – 5 PM • $80 service call, included in repair
          </p>
        </div>
      </section>

      {/* About + Services */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Local Appliance Experts in {city.name}</h2>
            <p className="text-gray-700 leading-relaxed mb-6" data-testid="city-intro">{city.luxury ? city.luxuryNote : city.intro}</p>
            {city.luxury && (
              <div data-testid="city-luxury-badge" className="bg-blue-950 text-white rounded-xl p-6 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Gem className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-lg font-bold">High-End Brand Specialists</h3>
                </div>
                <p className="text-blue-100 text-sm">Sub-Zero • Wolf • Miele • Thermador • Viking • Gaggenau • Speed Queen</p>
              </div>
            )}
            <div className="bg-green-50 border border-green-200 rounded-xl p-5">
              <p className="font-semibold text-green-800">$25 OFF your repair for new {city.name} customers — mention this website when you call.</p>
            </div>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Services We Offer in {city.name}</h3>
            <ul className="space-y-3 mb-8" data-testid="city-services-list">
              {city.services.map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Neighborhoods We Serve</h3>
            <div className="flex flex-wrap gap-2" data-testid="city-neighborhoods">
              {city.neighborhoods.map((n) => (
                <span key={n} className="bg-blue-50 text-blue-800 border border-blue-200 px-3 py-1.5 rounded-full text-sm font-medium">
                  <MapPin className="inline w-3 h-3 mr-1" />{n}
                </span>
              ))}
            </div>
            {city.microAreas && (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Also Covering</h3>
                <div className="flex flex-wrap gap-2" data-testid="city-micro-areas">
                  {city.microAreas.map((n) => (
                    <span key={n} className="bg-gray-100 text-gray-700 border border-gray-300 px-3 py-1.5 rounded-full text-sm font-medium">
                      <MapPin className="inline w-3 h-3 mr-1" />{n}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">What {city.name} Customers Say</h2>
          <div data-testid="city-testimonial" className="bg-white rounded-2xl p-8 shadow-md">
            <div className="flex gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <p className="text-gray-700 text-lg leading-relaxed mb-5">"{city.testimonial.text}"</p>
            <div className="border-t border-gray-200 pt-4">
              <p className="font-bold text-gray-900">{city.testimonial.name}</p>
              <p className="text-sm text-gray-500">{city.testimonial.area}, {city.name} • {city.testimonial.appliance}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Serving {city.name} and Surrounding Areas</h2>
          <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200">
            <iframe
              title={`Map of ${city.name}, FL`}
              data-testid="city-map"
              src={`https://maps.google.com/maps?q=${city.lat},${city.lng}&z=12&output=embed`}
              className="w-full h-80 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-blue-900 to-blue-800 text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Need an Appliance Fixed in {city.name}?</h2>
          <p className="text-blue-200 text-lg mb-8">Same-day slots available • Licensed & insured • Warranty on parts & labor</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:7866964010"
              onClick={() => trackEvent('click_to_call', { page: city.slug })}
              data-testid="city-cta-call-button"
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-bold inline-flex items-center justify-center gap-2 transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call 786-696-4010
            </a>
            <Link
              to="/book-online"
              onClick={() => trackEvent('begin_booking', { page: city.slug })}
              data-testid="city-cta-book-button"
              className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-bold transition-colors"
            >
              Book Online
            </Link>
          </div>
        </div>
      </section>

      {/* Footer lite */}
      <footer className="bg-gray-900 text-gray-400 py-8 text-center text-sm">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mb-4">
            {cities.map((c) => (
              <Link key={c.slug} to={cityPath(c)} data-testid={`city-footer-link-${c.slug}`} className={`hover:text-white transition-colors ${c.slug === city.slug ? 'text-white font-semibold' : ''}`}>
                {c.name}
              </Link>
            ))}
          </div>
          <p>&copy; {new Date().getFullYear()} Appliances Repair MCulai • Serving Miami to Boynton Beach • Licensed & Insured</p>
        </div>
      </footer>
    </div>
  );
}
